import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { ThemeProvider } from '@/components/ThemeProvider';
import { Sidebar } from '@/components/Sidebar';
import { ToolGrid } from '@/components/ToolGrid';
import { ToolLayout } from '@/components/ToolLayout';
import { CommandPalette } from '@/components/CommandPalette';
import { CookieBanner } from '@/components/CookieBanner';
import { Footer } from '@/components/Footer';
import { PrivacyPolicy } from '@/components/PrivacyPolicy';
import { Changelog } from '@/components/Changelog';
import { Toaster } from '@/components/ui/sonner';
import { getToolsByCategory, tools as allTools, getCategoryById, categories, type Category, type Tool } from '@/data/tools';
import { useFavorites } from '@/hooks/use-favorites';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
// Tool components are now lazy-loaded below for better code splitting and performance
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Menu, Search } from 'lucide-react';
import './index.css';

// Tool component map with lazy loading for better code splitting
const toolComponents: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  // Text tools
  base64: lazy(() => import('@/tools/text/Base64').then(m => ({ default: m.Base64Tool }))),
  base32: lazy(() => import('@/tools/text/Base32').then(m => ({ default: m.Base32Tool }))),
  'url-encoder': lazy(() => import('@/tools/text/UrlEncoder').then(m => ({ default: m.UrlEncoderTool }))),
  'case-converter': lazy(() => import('@/tools/text/CaseConverter').then(m => ({ default: m.CaseConverterTool }))),
  'lorem-ipsum': lazy(() => import('@/tools/text/LoremIpsum').then(m => ({ default: m.LoremIpsumTool }))),
  'text-counter': lazy(() => import('@/tools/text/TextCounter').then(m => ({ default: m.TextCounterTool }))),
  'string-manipulation': lazy(() => import('@/tools/text/StringManipulation').then(m => ({ default: m.StringManipulationTool }))),
  'text-to-binary-hex': lazy(() => import('@/tools/text/TextToBinaryHex').then(m => ({ default: m.TextToBinaryHexTool }))),
  'slug-generator': lazy(() => import('@/tools/text/SlugGenerator').then(m => ({ default: m.SlugGeneratorTool }))),
  'regex-replace': lazy(() => import('@/tools/text/RegexReplace').then(m => ({ default: m.RegexReplaceTool }))),
  'unicode-converter': lazy(() => import('@/tools/text/UnicodeConverter').then(m => ({ default: m.UnicodeConverterTool }))),
  'html-stripper': lazy(() => import('@/tools/text/HtmlStripper').then(m => ({ default: m.HtmlStripperTool }))),
  'html-encoder': lazy(() => import('@/tools/text/HtmlEncoder').then(m => ({ default: m.HtmlEncoderTool }))),
  'markdown-to-html': lazy(() => import('@/tools/text/MarkdownToHtml').then(m => ({ default: m.MarkdownToHtml }))),
  'text-template-engine': lazy(() => import('@/tools/text/TextTemplateEngine').then(m => ({ default: m.TextTemplateEngineTool }))),
  'remove-duplicates': lazy(() => import('@/tools/text/RemoveDuplicates').then(m => ({ default: m.RemoveDuplicates }))),
  'line-sorter': lazy(() => import('@/tools/text/LineSorter').then(m => ({ default: m.LineSorter }))),

  // JSON tools
  'json-formatter': lazy(() => import('@/tools/json/JsonFormatter').then(m => ({ default: m.JsonFormatterTool }))),
  'json-to-typescript': lazy(() => import('@/tools/json/JsonToTypescript').then(m => ({ default: m.JsonToTypeScriptTool }))),
  'json-path-tester': lazy(() => import('@/tools/json/JsonPathTester').then(m => ({ default: m.JsonPathTesterTool }))),
  'json-diff': lazy(() => import('@/tools/json/JsonDiff').then(m => ({ default: m.JsonDiffTool }))),
  'json-minifier': lazy(() => import('@/tools/json/JsonMinifier').then(m => ({ default: m.JsonMinifierTool }))),
  'json-schema-generator': lazy(() => import('@/tools/json/JsonSchemaGenerator').then(m => ({ default: m.JsonSchemaGeneratorTool }))),

  // Color tools
  'color-converter': lazy(() => import('@/tools/color/ColorConverter').then(m => ({ default: m.ColorConverterTool }))),
  'color-picker': lazy(() => import('@/tools/color/ColorPicker').then(m => ({ default: m.ColorPickerTool }))),
  'gradient-generator': lazy(() => import('@/tools/color/GradientGenerator').then(m => ({ default: m.GradientGeneratorTool }))),
  'css-color-names': lazy(() => import('@/tools/color/CssColorNames').then(m => ({ default: m.CssColorNamesTool }))),
  'tailwind-color-palette': lazy(() => import('@/tools/color/TailwindColorPalette').then(m => ({ default: m.TailwindColorPalette }))),
  'color-blindness-simulator': lazy(() => import('@/tools/color/ColorBlindnessSimulator').then(m => ({ default: m.ColorBlindnessSimulator }))),
  'color-palette-generator': lazy(() => import('@/tools/color/ColorPaletteGenerator').then(m => ({ default: m.ColorPaletteGenerator }))),
  'contrast-checker': lazy(() => import('@/tools/color/ContrastChecker').then(m => ({ default: m.ContrastChecker }))),
  'color-scheme-generator': lazy(() => import('@/tools/color/ColorSchemeGenerator').then(m => ({ default: m.ColorSchemeGenerator }))),
  'brand-color-extractor': lazy(() => import('@/tools/color/BrandColorExtractor').then(m => ({ default: m.BrandColorExtractor }))),
  'hex-alpha-converter': lazy(() => import('@/tools/color/AlphaColorConverter').then(m => ({ default: m.AlphaColorConverterTool }))),

  // Crypto tools
  'hash-generator': lazy(() => import('@/tools/crypto/HashGenerator').then(m => ({ default: m.HashGeneratorTool }))),
  'hmac-generator': lazy(() => import('@/tools/crypto/HmacGenerator').then(m => ({ default: m.HmacGeneratorTool }))),
  'uuid-generator': lazy(() => import('@/tools/crypto/UuidGenerator').then(m => ({ default: m.UuidGeneratorTool }))),
  'password-generator': lazy(() => import('@/tools/crypto/PasswordGenerator').then(m => ({ default: m.PasswordGeneratorTool }))),
  'password-strength-checker': lazy(() => import('@/tools/crypto/PasswordStrengthChecker').then(m => ({ default: m.PasswordStrengthCheckerTool }))),
  'random-number-generator': lazy(() => import('@/tools/crypto/RandomNumberGenerator').then(m => ({ default: m.RandomNumberGeneratorTool }))),
  'checksum-calculator': lazy(() => import('@/tools/crypto/ChecksumCalculator').then(m => ({ default: m.ChecksumCalculatorTool }))),
  'rsa-key-generator': lazy(() => import('@/tools/crypto/RsaKeyGenerator').then(m => ({ default: m.RsaKeyGeneratorTool }))),
  'bcrypt-generator': lazy(() => import('@/tools/crypto/BcryptGenerator').then(m => ({ default: m.BcryptGeneratorTool }))),
  'totp-generator': lazy(() => import('@/tools/crypto/TotpGenerator').then(m => ({ default: m.TotpGeneratorTool }))),
  'caesar-cipher': lazy(() => import('@/tools/crypto/CaesarCipher').then(m => ({ default: m.CaesarCipherTool }))),
  'aes-encryption': lazy(() => import('@/tools/crypto/AesEncryption').then(m => ({ default: m.AesEncryptionTool }))),

  // Misc tools
  'meta-tags-generator': lazy(() => import('@/tools/misc/MetaTagsGenerator').then(m => ({ default: m.MetaTagsGeneratorTool }))),
  'emoji-picker': lazy(() => import('@/tools/misc/EmojiPicker').then(m => ({ default: m.EmojiPickerTool }))),
  'ascii-table': lazy(() => import('@/tools/misc/AsciiTable').then(m => ({ default: m.AsciiTable }))),
  'html-entity-list': lazy(() => import('@/tools/misc/HtmlEntityList').then(m => ({ default: m.HtmlEntityListTool }))),
  'device-screen-sizes': lazy(() => import('@/tools/misc/DeviceScreenSizes').then(m => ({ default: m.DeviceScreenSizes }))),
  'barcode-generator': lazy(() => import('@/tools/misc/BarcodeGenerator').then(m => ({ default: m.BarcodeGenerator }))),
  'open-graph-preview': lazy(() => import('@/tools/misc/OpenGraphPreview').then(m => ({ default: m.OpenGraphPreviewTool }))),
  'qr-code-reader': lazy(() => import('@/tools/misc/QRCodeReaderTool').then(m => ({ default: m.QRCodeReaderTool }))),
  'google-fonts-browser': lazy(() => import('@/tools/misc/GoogleFontsBrowser').then(m => ({ default: m.GoogleFontsBrowser }))),

  // Data tools
  'xml-formatter': lazy(() => import('@/tools/data/XmlFormatterTool').then(m => ({ default: m.XmlFormatterTool }))),
  'sql-formatter': lazy(() => import('@/tools/data/SqlFormatter').then(m => ({ default: m.SqlFormatterTool }))),
  'data-faker': lazy(() => import('@/tools/data/DataFaker').then(m => ({ default: m.DataFakerTool }))),
  'api-mock-data-generator': lazy(() => import('@/tools/data/ApiMockDataGenerator').then(m => ({ default: m.ApiMockDataGenerator }))),
  'yaml-formatter': lazy(() => import('@/tools/data/YamlFormatter').then(m => ({ default: m.YamlFormatterTool }))),
  'excel-viewer': lazy(() => import('@/tools/data/ExcelViewer').then(m => ({ default: m.ExcelViewer }))),
  'log-analyzer': lazy(() => import('@/tools/data/LogAnalyzer').then(m => ({ default: m.LogAnalyzer }))),
  'table-converter': lazy(() => import('@/tools/data/TableConverter').then(m => ({ default: m.TableConverter }))),

  // Converter tools
  'timestamp-converter': lazy(() => import('@/tools/converters/TimestampConverter').then(m => ({ default: m.TimestampConverterTool }))),
  'unit-converter': lazy(() => import('@/tools/converters/UnitConverter').then(m => ({ default: m.UnitConverterTool }))),
  'font-to-base64': lazy(() => import('@/tools/converters/FontToBase64').then(m => ({ default: m.FontToBase64Tool }))),
  'file-to-base64': lazy(() => import('@/tools/converters/FileToBase64').then(m => ({ default: m.FileToBase64 }))),
  'currency-converter': lazy(() => import('@/tools/converters/CurrencyConverter').then(m => ({ default: m.CurrencyConverterTool }))),
  'json-to-xml': lazy(() => import('@/tools/converters/JsonToXml').then(m => ({ default: m.JsonToXmlTool }))),
  'toml-parser': lazy(() => import('@/tools/converters/TomlParser').then(m => ({ default: m.TomlParserTool }))),
  'sql-to-mongo': lazy(() => import('@/tools/converters/SqlToMongo').then(m => ({ default: m.SqlToMongoTool }))),
  'color-space-converter': lazy(() => import('@/tools/converters/ColorSpaceConverter').then(m => ({ default: m.ColorSpaceConverterTool }))),

  // Code tools
  'code-formatter': lazy(() => import('@/tools/code/CodeFormatter').then(m => ({ default: m.CodeFormatterTool }))),
  'cron-generator': lazy(() => import('@/tools/code/CronGenerator').then(m => ({ default: m.CronGeneratorTool }))),
  'box-shadow-generator': lazy(() => import('@/tools/code/BoxShadowGenerator').then(m => ({ default: m.BoxShadowGeneratorTool }))),
  'border-radius-generator': lazy(() => import('@/tools/code/BorderRadiusGenerator').then(m => ({ default: m.BorderRadiusGeneratorTool }))),
  'css-units-converter': lazy(() => import('@/tools/code/CssUnitsConverter').then(m => ({ default: m.CSSUnitsConverterTool }))),
  'grid-generator': lazy(() => import('@/tools/code/GridGenerator').then(m => ({ default: m.GridGeneratorTool }))),
  'flexbox-generator': lazy(() => import('@/tools/code/FlexboxGenerator').then(m => ({ default: m.FlexboxGeneratorTool }))),
  'easing-functions': lazy(() => import('@/tools/code/EasingFunctions').then(m => ({ default: m.EasingFunctionsTool }))),
  'git-cheatsheet': lazy(() => import('@/tools/code/GitCheatsheet').then(m => ({ default: m.GitCheatsheet }))),
  'http-status-codes': lazy(() => import('@/tools/code/HttpStatusCodes').then(m => ({ default: m.HttpStatusCodes }))),
  'mock-api-generator': lazy(() => import('@/tools/code/MockApiGenerator').then(m => ({ default: m.MockAPIGeneratorTool }))),
  'websocket-tester': lazy(() => import('@/tools/code/WebsocketTester').then(m => ({ default: m.WebSocketTester }))),
  'api-request-builder': lazy(() => import('@/tools/code/ApiRequestBuilder').then(m => ({ default: m.ApiRequestBuilderTool }))),
  'graphql-query-builder': lazy(() => import('@/tools/code/GraphQLQueryBuilder').then(m => ({ default: m.GraphQLQueryBuilder }))),

  // Image tools
  'exif-viewer': lazy(() => import('@/tools/image/ExifViewer').then(m => ({ default: m.ExifViewerTool }))),
  'placeholder-generator': lazy(() => import('@/tools/image/PlaceholderGenerator').then(m => ({ default: m.PlaceholderGeneratorTool }))),
  'image-cropper': lazy(() => import('@/tools/image/ImageCropper').then(m => ({ default: m.ImageCropperTool }))),
  'image-to-base64': lazy(() => import('@/tools/image/ImageToBase64').then(m => ({ default: m.ImageToBase64Tool }))),
  'image-compressor': lazy(() => import('@/tools/image/ImageCompressor').then(m => ({ default: m.ImageCompressorTool }))),
  'image-details': lazy(() => import('@/tools/image/ImageDetails').then(m => ({ default: m.ImageDetailsTool }))),
  'responsive-image-generator': lazy(() => import('@/tools/image/ResponsiveImageGenerator').then(m => ({ default: m.ResponsiveImageGeneratorTool }))),

  // Network tools
  'url-parser': lazy(() => import('@/tools/network/UrlParser').then(m => ({ default: m.UrlParserTool }))),
  'http-headers-viewer': lazy(() => import('@/tools/network/HttpHeadersViewer').then(m => ({ default: m.HttpHeadersViewerTool }))),

  // SEO tools
  'utm-builder': lazy(() => import('@/tools/seo/utm-builder').then(m => ({ default: m.UtmBuilderTool }))),
  'social-media-sizes': lazy(() => import('@/tools/seo/SocialMediaImageSizes').then(m => ({ default: m.SocialMediaImageSizesTool }))),
};

export function App() {
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [activeTool, setActiveTool] = useState<Tool | null>(null);
  const [showExternalTools, setShowExternalTools] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'type'>('name-asc');
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const { favorites, toggleFavorite, isFavorite } = useFavorites();

  // Ctrl+K / Cmd+K to open command palette
  useKeyboardShortcut(() => setCommandPaletteOpen(true), { key: 'k', ctrlKey: true, metaKey: true }, true);

  useEffect(() => {
    const handleNavigation = () => {
      const hash = window.location.hash.slice(1); // Remove leading #

      if (!hash || hash === '/') {
        // No hash or just /, show all tools
        setActiveCategory('all');
        setActiveTool(null);
        setShowPrivacyPolicy(false);
        setShowChangelog(false);
      } else if (hash === '/privacy' || hash === '/privacy-policy') {
        // Privacy policy route
        setShowPrivacyPolicy(true);
        setShowChangelog(false);
        setActiveTool(null);
      } else if (hash === '/changelog') {
        // Changelog route
        setShowChangelog(true);
        setShowPrivacyPolicy(false);
        setActiveTool(null);
      } else if (hash.startsWith('/tool/')) {
        // Legacy format: #/tool/json-formatter
        const toolPath = hash;
        const tool = allTools.find((t) => t.path === toolPath);
        if (tool && tool.type === 'internal') {
          setActiveTool(tool);
          setActiveCategory(tool.category);
          setShowPrivacyPolicy(false);
          setShowChangelog(false);
        }
      } else {
        // New format: #/category or #/category/tool-name
        const parts = hash.split('/').filter(Boolean);

        if (parts.length === 1) {
          // Just category: #/json
          const category = parts[0];
          if (categories.find((c) => c.id === category)) {
            setActiveCategory(category as Category);
            setActiveTool(null);
            setShowPrivacyPolicy(false);
            setShowChangelog(false);
          }
        } else if (parts.length === 2) {
          // Category + tool: #/json/json-formatter
          const [category, toolName] = parts;
          const toolPath = `/tool/${toolName}`;
          const tool = allTools.find((t) => t.path === toolPath);

          if (tool && tool.type === 'internal') {
            setActiveTool(tool);
            setActiveCategory(category as Category);
            setShowPrivacyPolicy(false);
            setShowChangelog(false);
          }
        }
      }
    };

    // Handle initial navigation on mount
    handleNavigation();

    window.addEventListener('hashchange', handleNavigation);
    return () => {
      window.removeEventListener('hashchange', handleNavigation);
    };
  }, []);

  const handleCategoryChange = (category: Category) => {
    setActiveCategory(category);
    setActiveTool(null);

    // Update URL with category in hash
    if (category === 'all') {
      window.location.hash = '/';
    } else {
      window.location.hash = `/${category}`;
    }
  };

  const handleToolClick = (tool: Tool) => {
    if (tool.type === 'external') {
      window.open(tool.url, '_blank', 'noopener,noreferrer');
    } else {
      setActiveTool(tool);
      // Extract tool name from path: /tool/json-formatter -> json-formatter
      const toolName = tool.path?.replace('/tool/', '');
      // New format: #/category/tool-name
      window.location.hash = `/${activeCategory}/${toolName}`;
    }
  };

  const handleBackToDashboard = () => {
    setActiveTool(null);
    // Go back to category view
    if (activeCategory === 'all') {
      window.location.hash = '/';
    } else {
      window.location.hash = `/${activeCategory}`;
    }
  };

  // Filter and sort tools based on search query, external tools toggle, and sort option
  const filteredTools = useMemo(() => {
    let categoryTools = getToolsByCategory(activeCategory, favorites);

    // Filter by external tools toggle
    if (!showExternalTools) {
      categoryTools = categoryTools.filter((tool) => tool.type === 'internal');
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      categoryTools = categoryTools.filter(
        (tool) => tool.name.toLowerCase().includes(query) || tool.description.toLowerCase().includes(query),
      );
    }

    // Sort tools
    const sorted = [...categoryTools].sort((a, b) => {
      if (sortBy === 'name-asc') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'name-desc') {
        return b.name.localeCompare(a.name);
      } else if (sortBy === 'type') {
        // Internal first, then external
        if (a.type === b.type) return a.name.localeCompare(b.name);
        return a.type === 'internal' ? -1 : 1;
      }
      return 0;
    });

    return sorted;
  }, [activeCategory, showExternalTools, searchQuery, sortBy, favorites]);

  // Calculate tool counts per category
  const toolCounts = useMemo(() => {
    const counts: Record<Category, number> = {
      all: 0,
      favorites: 0,
      converters: 0,
      text: 0,
      json: 0,
      data: 0,
      color: 0,
      image: 0,
      code: 0,
      crypto: 0,
      misc: 0,
      network: 0,
      seo: 0,
    };

    const toolsToCount = showExternalTools ? allTools : allTools.filter((t) => t.type === 'internal');

    toolsToCount.forEach((tool) => {
      counts[tool.category]++;
      counts.all++;

      // Count favorites
      if (favorites.includes(tool.id)) {
        counts.favorites++;
      }
    });

    return counts;
  }, [showExternalTools, favorites]);

  const renderToolPage = () => {
    if (!activeTool) return null;

    const ToolComponent = toolComponents[activeTool.id];

    if (ToolComponent) {
      return (
        <ToolLayout toolId={activeTool.id}>
          <Suspense fallback={
            <div className='flex items-center justify-center py-20'>
              <div className='flex flex-col items-center gap-3'>
                <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent' />
                <p className='text-sm text-muted-foreground'>Loading tool...</p>
              </div>
            </div>
          }>
            <ToolComponent />
          </Suspense>
        </ToolLayout>
      );
    }

    return (
      <div className='text-center py-20'>
        <p className='text-muted-foreground'>Tool not yet implemented</p>
      </div>
    );
  };

  return (
    <ThemeProvider>
      <Toaster />
      <CookieBanner />
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
      />
      <div className='flex h-screen overflow-hidden bg-background'>
        <Sidebar
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
          showExternalTools={showExternalTools}
          onExternalToolsToggle={setShowExternalTools}
          toolCounts={toolCounts}
          hideFixedMenuButton={!!activeTool || showPrivacyPolicy || showChangelog}
        />

        <main className='flex-1 flex flex-col overflow-hidden'>
          {showChangelog ?
            <div className='flex-1 overflow-y-auto'>
              <div className='min-h-full flex flex-col'>
                {/* Mobile header bar with menu + back button */}
                <div className='lg:hidden sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b px-4 py-3'>
                  <div className='flex items-center gap-3'>
                    <Button
                      variant='outline'
                      size='icon'
                      onClick={() => {
                        // Open sidebar - we need to trigger it
                        document.querySelector<HTMLButtonElement>('.lg\\:hidden.fixed button')?.click();
                      }}
                      className='h-10 w-10 shrink-0'
                    >
                      <Menu className='h-5 w-5' />
                    </Button>
                    <Button
                      variant='ghost'
                      onClick={handleBackToDashboard}
                      className='gap-2 flex-1 justify-start'
                    >
                      <ArrowLeft className='h-4 w-4' />
                      <span className='truncate'>Back to Dashboard</span>
                    </Button>
                  </div>
                </div>

                <div className='container mx-auto px-4 py-8 lg:px-8 lg:py-12 lg:ml-0 max-w-full flex-1'>
                  <div className='mx-auto max-w-4xl'>
                    {/* Desktop back button */}
                    <Button
                      variant='ghost'
                      onClick={handleBackToDashboard}
                      className='mb-6 gap-2 hidden lg:flex'
                    >
                      <ArrowLeft className='h-4 w-4' />
                      Back to Dashboard
                    </Button>
                    <Changelog />
                  </div>
                </div>
                <Footer />
              </div>
            </div>
            : showPrivacyPolicy ?
              <div className='flex-1 overflow-y-auto'>
                <div className='min-h-full flex flex-col'>
                  {/* Mobile header bar with menu + back button */}
                  <div className='lg:hidden sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b px-4 py-3'>
                    <div className='flex items-center gap-3'>
                      <Button
                        variant='outline'
                        size='icon'
                        onClick={() => {
                          // Open sidebar - we need to trigger it
                          document.querySelector<HTMLButtonElement>('.lg\\:hidden.fixed button')?.click();
                        }}
                        className='h-10 w-10 shrink-0'
                      >
                        <Menu className='h-5 w-5' />
                      </Button>
                      <Button
                        variant='ghost'
                        onClick={handleBackToDashboard}
                        className='gap-2 flex-1 justify-start'
                      >
                        <ArrowLeft className='h-4 w-4' />
                        <span className='truncate'>Back to Dashboard</span>
                      </Button>
                    </div>
                  </div>

                  <div className='container mx-auto px-4 py-8 lg:px-8 lg:py-12 lg:ml-0 max-w-full flex-1'>
                    <div className='mx-auto'>
                      {/* Desktop back button */}
                      <Button
                        variant='ghost'
                        onClick={handleBackToDashboard}
                        className='mb-6 gap-2 hidden lg:flex'
                      >
                        <ArrowLeft className='h-4 w-4' />
                        Back to Dashboard
                      </Button>
                      <PrivacyPolicy />
                    </div>
                  </div>
                  <Footer />
                </div>
              </div>
              : activeTool ?
                <div className='flex-1 overflow-y-auto'>
                  <div className='min-h-full flex flex-col'>
                    {/* Mobile header bar with menu + back button */}
                    <div className='lg:hidden sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b px-4 py-3'>
                      <div className='flex items-center gap-3'>
                        <Button
                          variant='outline'
                          size='icon'
                          onClick={() => {
                            // Open sidebar - we need to trigger it
                            document.querySelector<HTMLButtonElement>('.lg\\:hidden.fixed button')?.click();
                          }}
                          className='h-10 w-10 shrink-0'
                        >
                          <Menu className='h-5 w-5' />
                        </Button>
                        <Button
                          variant='ghost'
                          onClick={handleBackToDashboard}
                          className='gap-2 flex-1 justify-start'
                        >
                          <ArrowLeft className='h-4 w-4' />
                          <span className='truncate'>Back to Dashboard</span>
                        </Button>
                      </div>
                    </div>

                    <div className='container mx-auto px-4 py-8 lg:px-8 lg:py-12 lg:ml-0 max-w-full flex-1'>
                      <div className='mx-auto'>
                        {/* Desktop back button */}
                        <Button
                          variant='ghost'
                          onClick={handleBackToDashboard}
                          className='mb-6 gap-2 hidden lg:flex'
                        >
                          <ArrowLeft className='h-4 w-4' />
                          Back to Dashboard
                        </Button>
                        {renderToolPage()}
                      </div>
                    </div>
                    <Footer />
                  </div>
                </div>
                : <>
                  <div className='flex-1 overflow-y-auto'>
                    <div className='min-h-full flex flex-col'>
                      <div className='container mx-auto px-4 pt-16 lg:px-8 lg:pt-12 lg:ml-0 shrink-0 max-w-full'>
                        <div className='mb-8'>
                          <h2 className='text-3xl font-bold mb-2'>
                            {activeCategory === 'all' ?
                              'All Tools'
                              : `${activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)} Tools`}
                          </h2>
                          <p className='text-muted-foreground'>{getCategoryById(activeCategory)?.description}</p>
                        </div>

                        {/* Search and Sort Controls */}
                        <div className='flex flex-col sm:flex-row gap-4 mb-6'>
                          <div className='relative flex-1'>
                            <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                            <Input
                              type='text'
                              placeholder='Search tools...'
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className='pl-9'
                            />
                          </div>
                          <Select
                            value={sortBy}
                            onValueChange={(value) => setSortBy(value as typeof sortBy)}
                          >
                            <SelectTrigger className='w-full sm:w-[200px]'>
                              <SelectValue placeholder='Sort by' />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value='name-asc'>Name (A-Z)</SelectItem>
                              <SelectItem value='name-desc'>Name (Z-A)</SelectItem>
                              <SelectItem value='type'>Type (Internal first)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className='container mx-auto px-4 pb-8 lg:px-8 lg:pb-12 lg:ml-0 max-w-full flex-1'>
                        {filteredTools.length > 0 ?
                          <ToolGrid
                            tools={filteredTools}
                            onToolClick={handleToolClick}
                            isFavorite={isFavorite}
                            onToggleFavorite={toggleFavorite}
                          />
                          : <div className='text-center py-20'>
                            <p className='text-muted-foreground'>
                              {searchQuery.trim() ?
                                `No tools found matching "${searchQuery}"`
                                : activeCategory === 'favorites' ?
                                  'No favorite tools yet. Click the star icon on any tool to add it to favorites.'
                                  : 'No tools available in this category'}
                            </p>
                          </div>
                        }
                      </div>
                      <Footer />
                    </div>
                  </div>
                </>
          }
        </main>
      </div >
    </ThemeProvider >
  );
}

export default App;
